import React from "react";
import { X } from "lucide-react";
import "../../ucat-styles.css";

/* =========================
   Button
========================= */

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?:
  | "primary"
  | "secondary"
  | "outline"
  | "danger"
  | "ghost"
  | "pearson-nav";
  size?: "sm" | "md" | "lg";
  fontWeight?: 100 | 200 | 300 | 400 | 500 | 600 | 700  ;
}

export const Button: React.FC<ButtonProps> = ({
  children,
  variant = "primary",
  size = "md",
  className = "",
  fontWeight = null,
  ...props
}) => {
  let baseStyle;
  if (fontWeight) {
    baseStyle =
      `inline-flex items-center justify-center font-[${fontWeight}] transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed`;
  }
  else {
    baseStyle =
      "inline-flex items-center justify-center font-medium transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed";
  }

  const variants = {
    primary:
      "bg-transparent text-white hover:text-[#FFFF00] border border-white px-3 py-1 text-[13px]",

    secondary:
      "bg-gray-200 text-gray-900 hover:text-[#FFFF00] hover:bg-gray-300 border border-gray-300",

    outline:
      "border border-white text-white hover:text-[#FFFF00] hover:bg-white/10",

    danger:
      "bg-transparent text-white border border-white hover:bg-white/10 text-[13px] px-3 py-1",

    ghost: "bg-transparent text-white hover:text-[#FFFF00] hover:bg-white/10",

    "pearson-nav":
      "bg-transparent text-white hover:text-[#FFFF00] border-l-2 border-r-2 border-white px-3 py-1 text-[13px]"

  };

  const sizes = {
    sm: "px-2 py-1",
    md: "px-2 py-2",
    lg: "px-4 py-3",
  };

  return (
    <button
      className={`${baseStyle} ${variants[variant]} ${sizes[size]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};
/* =========================
   Modal
========================= */

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  width?: string;
  isDraggable?: boolean;
  showBackdropBlur?: boolean;
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  width = "max-w-lg",
  isDraggable = false,
  showBackdropBlur = true,
}) => {
  const [position, setPosition] = React.useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = React.useState(false);
  const [dragStart, setDragStart] = React.useState({ x: 0, y: 0 });

  React.useEffect(() => {
    if (!isOpen) {
      setPosition({ x: 0, y: 0 });
    }
  }, [isOpen]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!isDraggable) return;
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  React.useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        setPosition({
          x: e.clientX - dragStart.x,
          y: e.clientY - dragStart.y,
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    }
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, dragStart]);

  if (!isOpen) return null;

  return (
    <div className={`ucat-module fixed inset-0 z-50 flex items-center justify-center bg-black/10 ${showBackdropBlur ? "backdrop-blur-sm" : ""} p-4`}>
      <div
        style={{
          transform: `translate(${position.x}px, ${position.y}px)`,
        }}
        className={`bg-white shadow-2xl w-full ${width} max-h-[90vh] flex flex-col border border-gray-400`}
      >
        {/* Header */}
        <div
          onMouseDown={handleMouseDown}
          style={{ cursor: isDraggable ? (isDragging ? "grabbing" : "grab") : "default" }}
          className="flex items-center justify-between p-2 px-3 bg-[#006DAA] text-white border-b border-[#003B70] select-none f-tahoma"
        >
          <h3 className="font-bold text-[15px] flex items-center gap-2 f-tahoma">
            {title}
          </h3>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 rounded p-1 f-tahoma"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="p-2 overflow-y-auto flex-1 bg-[#006DAA] text-gray-900">
          <div className="p-2 bg-[#006DAA]"
            style={{ minHeight: "200px" }}>
            {children}
          </div>
        </div>

        {/* Footer */}
        {/* <div className="p-2 bg-gray-100 border-t flex justify-end">
          <button
            onClick={onClose}
            style={{ fontSize: "14px" }}
            className="px-4 py-1 bg-[#006DAA] text-white font-bold shadow-sm hover:bg-[#004a80]"
          >
            Close
          </button>
        </div> */}
      </div>
    </div>
  );
};

/* =========================
   Badge
========================= */


